import React from 'react';
import { T, NUMERIC, money } from '../customer/customers.theme';
import { Dialog, DialogHeader, GhostButton, PrimaryButton } from '../customer/CustomersUI';
import { formatRowDateTime } from '../../utils/appDate';
import { isSettled, itemCount, orderItems } from './recentOrdersApi';

/**
 * One of a waiter's recent orders, and the two things they can do with it.
 *
 * Tapping a card does NOT load it straight onto the pad. A waiter halfway
 * through building a new order would lose it without being asked, and the card
 * is small enough to tap by accident while scrolling the strip. So the tap
 * opens this, which shows what the order actually is before anything happens
 * to it.
 *
 * There is no kitchen-ticket button here, deliberately. An edit sends the
 * stations only what was ADDED — the server diffs the order before saving —
 * but a full ticket reprint would re-send food that is already cooking. A
 * manager has that button on the Orders screen, where there is time to judge
 * it; a waiter tapping it mid-rush would double a plate.
 */

const COPY = {
  title: (n) => `Order #${n}`,
  edit: 'Edit order',
  confirmEdit: 'Replace and edit',
  print: 'Print receipt',
  total: 'Total',
  settled: 'This order is settled. It can be reprinted but not changed.',
  replaceCart: 'You have items in the cart. Loading this order will replace them.',
};

export default function RecentOrderDialog({ order, cartCount, onClose, onEdit, onPrint }) {
  const settled = isSettled(order);
  const items = orderItems(order);

  const [confirming, setConfirming] = React.useState(false);

  const startEdit = () => {
    // Asked once, and only when there is something to lose.
    if (cartCount > 0 && !confirming) {
      setConfirming(true);
      return;
    }
    onEdit(order);
  };

  return (
    <Dialog onClose={onClose} width={430}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader title={COPY.title(order.orderNumber)} onClose={onClose} />

        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
            paddingTop: 14,
            fontSize: 12,
            color: T.inkFaint,
          }}
        >
          <span style={{ flex: 1, ...NUMERIC }}>{formatRowDateTime(order.date)}</span>
          <span>{String(order.orderType || '').replace('dine-in', 'Dine-in')}</span>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: 14,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusPanel,
            overflow: 'hidden',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {items.map((item, index) => (
            <div
              key={`${item.menuItem || item.name}-${index}`}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                padding: '10px 14px',
                background: index % 2 ? T.sunk : T.surface,
              }}
            >
              <span
                style={{
                  minWidth: 26,
                  fontSize: 13,
                  fontWeight: 700,
                  color: T.inkSoft,
                  ...NUMERIC,
                }}
              >
                {item.quantity}
              </span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13.5,
                  color: T.ink,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.name}
              </span>
              <span style={{ fontSize: 13, color: T.ink, ...NUMERIC }}>
                {money(Number(item.price || 0) * Number(item.quantity || 0))}
              </span>
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
            padding: '14px 2px 0',
          }}
        >
          <span style={{ flex: 1, fontSize: 12, color: T.inkFaint }}>
            {itemCount(order)} · {COPY.total}
          </span>
          <span style={{ fontSize: 17, fontWeight: 700, color: T.ink, ...NUMERIC }}>
            {money(order.total)}
          </span>
        </div>

        {settled ? (
          <div
            style={{
              marginTop: 14,
              padding: '10px 14px',
              background: T.paidTint,
              borderRadius: T.radiusControl,
              fontSize: 12,
              color: T.paid,
              lineHeight: 1.5,
            }}
          >
            {COPY.settled}
          </div>
        ) : null}

        {confirming ? (
          <div
            style={{
              marginTop: 14,
              padding: '10px 14px',
              background: T.pendingTint,
              border: `1px solid ${T.pendingBorder}`,
              borderRadius: T.radiusControl,
              fontSize: 12,
              color: T.pendingInk,
              lineHeight: 1.5,
            }}
          >
            {COPY.replaceCart}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <GhostButton style={{ flex: 1 }} onClick={() => onPrint(order)}>
            {COPY.print}
          </GhostButton>

          {/* Absent rather than disabled on a settled order: a greyed button
              invites a tap and then explains itself, which is slower than not
              being there. The note above says why. */}
          {settled ? null : (
            <PrimaryButton style={{ flex: 1 }} onClick={startEdit}>
              {confirming ? COPY.confirmEdit : COPY.edit}
            </PrimaryButton>
          )}
        </div>
      </div>
    </Dialog>
  );
}
